import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';

import {catchError, filter, map, NEVER, Observable, of, switchMap, take, takeUntil, throwError} from 'rxjs';
import {environment} from "@environments/environment";
import {AuthService, parseAuthData} from "@core/authentication/auth.service";
import {HEADER_STATUS_CODE, REQUEST_HEADER_APP_VERSION, REQUEST_HEADER_AUTHORIZATION} from "@core/constants";
import {uriConsts} from "@shared/constants";

// Chỉ handshake đăng nhập và khôi phục phiên được bỏ token.
// Callback kết nối kênh cần token của user đang đăng nhập để BE kiểm tra CSRF.
// /auth/callback của đăng nhập Nhanh trang /signin tự gắn jwt của phiên đang đăng nhập (LoginCallBackComponent) — giữ nguyên.
const AUTH_ENDPOINTS_WITHOUT_ACCESS_TOKEN = new Set<string>([
  uriConsts.auth.callback,
  uriConsts.auth.refresh,
]);

// Handshake đăng nhập: bị 403 nghĩa là phiên BE cũ còn đăng nhập nhưng FE không còn đúng jwt
const AUTH_HANDSHAKE_ENDPOINTS = new Set<string>([
  uriConsts.auth.login,
  uriConsts.auth.callback,
]);

// Đánh dấu request đã thử phục hồi 403 một lần — tránh lặp vô hạn
const RECOVERED_FROM_FORBIDDEN = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    @Inject(AuthService) private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl = req.url.startsWith(environment.baseUrlApi);
    // Ads portal gắn token riêng qua AdsAuthInterceptor
    if (!isApiUrl || environment.isAdsModule) {
      return next.handle(req);
    }

    const path = req.url.slice(environment.baseUrlApi.length).split('?')[0];
    const headers: { [name: string]: string } = {
      [REQUEST_HEADER_APP_VERSION]: environment.apiVersion.toString()
    };
    const authorizationToken = this.authService.getAuthorizationToken();
    if (authorizationToken && !AUTH_ENDPOINTS_WITHOUT_ACCESS_TOKEN.has(path)) {
      headers[REQUEST_HEADER_AUTHORIZATION] = authorizationToken;
    }

    req = req.clone({
      setHeaders: headers
    });

    return next.handle(req).pipe(
      // Response của phiên cũ không được ghi lại usr sau khi logout/reset auth.
      takeUntil(path === uriConsts.logout ? NEVER : this.authService.onAuthCleared$),
      catchError((error: HttpErrorResponse) => {
        // BE chỉ chặn CSRF với request khác GET; mỗi request chỉ thử phục hồi một lần
        if (error.status !== HEADER_STATUS_CODE.FORBIDDEN || req.method === 'GET'
          || this.authService.isLoggingOut || req.context.get(RECOVERED_FROM_FORBIDDEN)) {
          return throwError(() => error);
        }

        if (AUTH_HANDSHAKE_ENDPOINTS.has(path)) {
          return this.recoverAuthHandshake(req, next);
        }

        // POST /auth/logout bị 403 đã có GET /auth/logout dự phòng trong AuthService.logout()
        if (path !== uriConsts.logout && this.authService.getUid()) {
          return this.resyncCsrf(req, next, error);
        }

        return throwError(() => error);
      }),
    );
  }

  /**
   * POST /auth/login|/auth/callback bị 403: phiên BE cũ còn đăng nhập nhưng FE không còn đúng jwt nên BE chặn
   * CSRF trước khi action chạy. GET /auth/logout không cần CSRF: huỷ phiên cũ rồi thử lại đúng một lần.
   */
  private recoverAuthHandshake(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Token lưu từ phiên cũ có thể lệch CSRF. Xóa riêng cache user, giữ ngôn ngữ/theme.
    this.authService.clearCachedAuthUser();

    // Giữ Authorization cho GET logout: dev nhận phiên qua phần sessionId của jwt; production GET không kiểm CSRF
    const serverLogout = new HttpRequest('GET', environment.baseUrlApi + uriConsts.logout, {headers: req.headers});
    const retryRequest = req.clone({
      headers: req.headers.delete(REQUEST_HEADER_AUTHORIZATION),
      context: req.context.set(RECOVERED_FROM_FORBIDDEN, true),
    });

    return next.handle(serverLogout).pipe(
      filter(event => event.type === HttpEventType.Response),
      take(1),
      switchMap(() => next.handle(retryRequest)),
      takeUntil(this.authService.onAuthCleared$),
    );
  }

  /**
   * Request khác bị 403 khi FE đang đăng nhập: jwt (csrf) có thể lệch với phiên BE (đăng nhập lại ở luồng khác,
   * giao diện V1...). GET /auth/me không cần CSRF: cùng user mà jwt khác thì cập nhật jwt rồi thử lại đúng một lần.
   * Còn lại (403 do không có quyền, khác user, /auth/me lỗi) trả nguyên lỗi ban đầu.
   */
  private resyncCsrf(req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    // Giữ Authorization: dev nhận phiên qua phần sessionId của jwt; production GET không kiểm CSRF
    const authMe = new HttpRequest('GET', environment.baseUrlApi + uriConsts.auth.me, {headers: req.headers});

    return next.handle(authMe).pipe(
      filter((event): event is HttpResponse<any> => event.type === HttpEventType.Response),
      take(1),
      map(response => Number(response.body?.code) === 1 ? parseAuthData(response.body?.data) : null),
      catchError(() => of(null)),
      switchMap(auth => {
        const isSameUser = !!auth?.uid && String(auth.uid) === String(this.authService.getUid());
        if (!auth?.jwt || !isSameUser || `Bearer ${auth.jwt}` === this.authService.getAuthorizationToken()) {
          return throwError(() => error);
        }

        this.authService.setJwt(auth.jwt);
        return next.handle(req.clone({
          setHeaders: {[REQUEST_HEADER_AUTHORIZATION]: this.authService.getAuthorizationToken()},
          context: req.context.set(RECOVERED_FROM_FORBIDDEN, true),
        }));
      }),
      takeUntil(this.authService.onAuthCleared$),
    );
  }
}
